import { useState } from "react";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { db } from "../../firebase/config";
import { useCollection } from "../../hooks/useCollection";
import ImageUploader from "../../components/admin/ImageUploader";

const CATEGORIES = [
  { value: "app", label: "Seminar" },
  { value: "card", label: "Study" },
  { value: "web", label: "Travel" },
];

const emptyForm = {
  title: "",
  category: "app",
  imageUrl: "",
  order: 0,
};

export default function PortfolioAdmin() {
  const { items, loading } = useCollection("portfolio", "order", "asc");
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const reset = () => {
    setForm(emptyForm);
    setEditingId(null);
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setForm({
      title: item.title || "",
      category: item.category || "app",
      imageUrl: item.imageUrl || "",
      order: item.order ?? 0,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        order: Number(form.order) || 0,
        updatedAt: serverTimestamp(),
      };
      if (editingId) {
        await updateDoc(doc(db, "portfolio", editingId), payload);
      } else {
        await addDoc(collection(db, "portfolio"), {
          ...payload,
          createdAt: serverTimestamp(),
        });
      }
      reset();
    } catch (err) {
      alert(`บันทึกไม่สำเร็จ: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`ลบ "${item.title}"?`)) return;
    await deleteDoc(doc(db, "portfolio", item.id));
  };

  const categoryLabel = (v) =>
    CATEGORIES.find((c) => c.value === v)?.label || v;

  return (
    <div>
      <h2 className="mb-4">จัดการ Gallery</h2>

      <div className="card shadow-sm mb-4">
        <div className="card-body">
          <h5 className="card-title mb-3">
            {editingId ? "แก้ไขภาพ" : "เพิ่มภาพใหม่"}
          </h5>
          <form onSubmit={handleSubmit}>
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label">ชื่อภาพ</label>
                <input
                  className="form-control"
                  value={form.title}
                  onChange={update("title")}
                  required
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">หมวด</label>
                <select
                  className="form-select"
                  value={form.category}
                  onChange={update("category")}
                >
                  {CATEGORIES.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-2">
                <label className="form-label">ลำดับ</label>
                <input
                  type="number"
                  className="form-control"
                  value={form.order}
                  onChange={update("order")}
                />
              </div>
              <div className="col-12">
                <label className="form-label">รูปภาพ</label>
                <ImageUploader
                  value={form.imageUrl}
                  onChange={(url) => setForm({ ...form, imageUrl: url })}
                />
              </div>
            </div>
            <div className="mt-3 d-flex gap-2">
              <button className="btn btn-primary" disabled={saving}>
                {saving ? "กำลังบันทึก..." : editingId ? "บันทึก" : "เพิ่ม"}
              </button>
              {editingId && (
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={reset}
                >
                  ยกเลิก
                </button>
              )}
            </div>
          </form>
        </div>
      </div>

      <div className="card shadow-sm">
        <div className="card-body">
          <h5 className="card-title mb-3">รายการ ({items.length})</h5>
          {loading ? (
            <div>กำลังโหลด...</div>
          ) : items.length === 0 ? (
            <div className="text-muted">ยังไม่มีรายการ</div>
          ) : (
            <table className="table align-middle">
              <thead>
                <tr>
                  <th style={{ width: 100 }}>รูป</th>
                  <th>ชื่อ</th>
                  <th>หมวด</th>
                  <th style={{ width: 80 }}>ลำดับ</th>
                  <th style={{ width: 180 }}></th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id}>
                    <td>
                      {item.imageUrl && (
                        <img
                          src={item.imageUrl}
                          alt=""
                          style={{ width: 80, height: 60, objectFit: "cover" }}
                          className="rounded"
                        />
                      )}
                    </td>
                    <td>{item.title}</td>
                    <td>{categoryLabel(item.category)}</td>
                    <td>{item.order ?? 0}</td>
                    <td>
                      <button
                        className="btn btn-sm btn-outline-primary me-2"
                        onClick={() => handleEdit(item)}
                      >
                        แก้ไข
                      </button>
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleDelete(item)}
                      >
                        ลบ
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
